import tailwindConfig from "tailwind.config";
import resolveConfig from "tailwindcss/resolveConfig";

import {
  PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
  PlayerDetailSeasonCategoricalScoresFragment,
} from "@/generated/graphql";

/**
 * Score data structure for player metrics containing season categorical scores or weighted averages
 */
export interface IScore {
  [k: string]:
    | number
    | "PlayerSeasonCategoricalScore"
    | "PlayerSeasonWeightedAvgMetrics"
    | null;
}

/**
 * Data structure for season-based player metrics with dynamic player-specific keys
 */
interface IData {
  [key: string]: number;
  season: number;
}

/**
 * Keys carried by a season-scored GraphQL row that are transport metadata
 * rather than metric values, and are dropped by {@link cleanSeasonScores}.
 * @remarks
 * `ggUnifiedSeasonKey` is deliberately NOT in this list — see
 * {@link cleanSeasonScores}.
 */
const NON_SCORE_KEYS: readonly string[] = ["__typename", "seasonId"];

/**
 * Strips transport metadata and null values from a player's season-scored rows
 * while preserving `ggUnifiedSeasonKey`, the season join key.
 * @param rows - Raw season rows from the player query (categorical scores or weighted averages).
 * @returns Cleaned rows safe to hand to {@link getCategoryMetric}.
 * @remarks
 * SE-7494: two player-detail containers each hand-rolled this cleaning step and
 * dropped `ggUnifiedSeasonKey` alongside `__typename`, treating it as noise.
 * {@link createDataForCurrentPlayer} requires exactly that field to key a
 * season, so it returned `[]` on every call from those containers and the
 * player's own line was declared in `yKeys` but never drawn — while the peer
 * averages, read from the raw query payload, plotted normally.
 *
 * The two sides of that contract now meet in this one function: the join key is
 * data, not metadata, and callers clean through here rather than by
 * coincidence.
 */
export const cleanSeasonScores = <T extends object>(
  rows: readonly (T | null | undefined)[] | null | undefined
): IScore[] =>
  (rows ?? []).map(
    row =>
      Object.fromEntries(
        Object.entries(row ?? {}).filter(
          ([key, value]) => value !== null && !NON_SCORE_KEYS.includes(key)
        )
      ) as IScore
  );

/**
 * Arithmetic mean of a non-empty list of numbers.
 * @param values - Values to average; callers must guarantee at least one.
 * @returns The mean.
 */
const mean = (values: readonly number[]): number =>
  values.reduce((sum, v) => sum + v, 0) / values.length;

export const createDataForCurrentPlayer = (
  scores: IScore[],
  playerId: string,
  key: keyof PlayerDetailSeasonCategoricalScoresFragment | "epm"
): IData[] =>
  scores.reduce<IData[]>((result, category) => {
    const score = category?.[key === "epm" ? "seasonalGpm" : key];
    const seasonKey = category?.ggUnifiedSeasonKey;
    if (
      typeof score === "number" &&
      typeof seasonKey === "number" &&
      seasonKey
    ) {
      return [
        ...result,
        {
          season: seasonKey,
          [`${key}_${playerId}`]: score,
        },
      ];
    }
    return result;
  }, []);

export const createDataForAllPlayers = (
  playersData: PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery | undefined,
  key: keyof PlayerDetailSeasonCategoricalScoresFragment | "epm"
): IData[] => {
  if (!playersData?.listPlayersByTeamIdsAndPositionIds.edges) return [];

  return playersData.listPlayersByTeamIdsAndPositionIds.edges.flatMap(e => {
    const player = e.node;
    const metrics =
      key === "epm"
        ? player.seasonWeightedAvgMetrics
        : player.seasonCategoricalScores;

    if (!metrics) return [];

    return metrics
      .filter(metric => {
        if (!metric?.ggUnifiedSeasonKey) return false;
        const metricKey = key === "epm" ? "seasonalGpm" : key;
        const value = (metric as Record<string, unknown>)[metricKey];
        return typeof value === "number";
      })
      .map(metric => {
        const metricKey = key === "epm" ? "seasonalGpm" : key;
        const value = (metric as Record<string, unknown>)[metricKey] as number;
        return {
          season: metric!.ggUnifiedSeasonKey,
          [`${key}_${player.id}`]: value,
        };
      });
  });
};

export const getColorForKey = (
  yKey: string,
  key: keyof PlayerDetailSeasonCategoricalScoresFragment | "epm",
  playerId: string
): string => {
  const fullConfig = resolveConfig(tailwindConfig);
  const themeColors = (
    fullConfig.theme.colors as unknown as Record<string, Record<string, string>>
  ).chart;
  switch (yKey) {
    case `${key}_${playerId}`:
      return themeColors.player;
    case "horizontalLine":
      return themeColors.horizontalLine;
    default:
      return themeColors.other;
  }
};

/**
 * Groups every peer player's values by season, excluding the current player.
 * @param playersData - Query result with all players in the same team and position.
 * @param key - Metric key to collect.
 * @param excludePlayerId - Current player, whose values are contributed separately.
 * @returns Season number to the list of peer values recorded for it.
 * @remarks
 * The current player is matched on the exact generated key (`<key>_<id>`)
 * rather than a substring of the id, so a peer whose id merely contains the
 * current player's id is not silently dropped from the peer set.
 */
const collectPeerValuesBySeason = (
  playersData: PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery | undefined,
  key: keyof PlayerDetailSeasonCategoricalScoresFragment | "epm",
  excludePlayerId: string
): Record<number, number[]> => {
  const currentPlayerKey = `${key}_${excludePlayerId}`;

  return createDataForAllPlayers(playersData, key).reduce<
    Record<number, number[]>
  >((acc, datum) => {
    const values = Object.entries(datum)
      .filter(
        ([k, v]) =>
          k !== "season" &&
          k !== currentPlayerKey &&
          typeof v === "number" &&
          Number.isFinite(v)
      )
      .map(([, v]) => v);

    if (values.length === 0) return acc;
    return {
      ...acc,
      [datum.season]: [...(acc[datum.season] ?? []), ...values],
    };
  }, {});
};

export const getCategoryMetric = (
  scores: IScore[],
  playersData: PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery | undefined,
  playerId: string,
  key: keyof PlayerDetailSeasonCategoricalScoresFragment | "epm"
) => {
  if (!scores) return;

  // Get current player data
  const currentPlayerData = createDataForCurrentPlayer(scores, playerId, key);

  // Peer values per season (position peers only — the current player is added
  // back explicitly below, so he can never be double-counted).
  const peerValuesBySeason = collectPeerValuesBySeason(
    playersData,
    key,
    playerId
  );

  // Get all seasons from both datasets
  const allSeasons = [
    ...new Set([
      ...currentPlayerData.map(d => d.season),
      ...Object.keys(peerValuesBySeason).map(Number),
    ]),
  ].sort((a, b) => a - b);

  // Build final data structure with only 3 lines
  const currentPlayerKey = `${key}_${playerId}`;
  const positionKey = "positionAverage";
  const teamAvgKey = "teamAverage";

  const formatDatas: IData[] = allSeasons.map(season => {
    const playerValue = currentPlayerData.find(d => d.season === season)?.[
      currentPlayerKey
    ];
    const peerValues = peerValuesBySeason[season] ?? [];

    /*
     * SE-7494: the team average is the position peers PLUS the current player,
     * as the surface's own legend claims. It used to be read off the peer query
     * alone, which excluded the player whenever that query did not happen to
     * contain him — leaving `teamAverage` numerically identical to
     * `positionAverage`.
     *
     * A season with nothing to average omits the point rather than plotting a
     * literal 50, which rendered indistinguishably from a measured value.
     */
    const teamValues = [
      ...peerValues,
      ...(typeof playerValue === "number" ? [playerValue] : []),
    ];
    const teamAvgValue =
      key === "epm" ? 0 : teamValues.length > 0 ? mean(teamValues) : undefined;

    return {
      season,
      ...(typeof playerValue === "number"
        ? { [currentPlayerKey]: playerValue }
        : {}),
      ...(peerValues.length > 0 ? { [positionKey]: mean(peerValues) } : {}),
      ...(typeof teamAvgValue === "number"
        ? { [teamAvgKey]: teamAvgValue }
        : {}),
    };
  });

  // Only 3 yKeys: current player, position average, team average
  const yKeys = [currentPlayerKey, positionKey, teamAvgKey];

  const fullConfig = resolveConfig(tailwindConfig);
  const themeColors = (
    fullConfig.theme.colors as unknown as Record<string, Record<string, string>>
  ).chart;

  const colors: Record<string, string> = {
    [currentPlayerKey]: themeColors.player,
    [positionKey]: themeColors.other,
    [teamAvgKey]: themeColors.horizontalLine,
  };

  return {
    data: formatDatas,
    yKeys,
    colors,
  };
};
